import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HandThumbUpIcon, HandThumbDownIcon, MinusIcon } from "@heroicons/react/24/solid";
import TableWithServerOrdering from "../../components/Table/TableWithServerOrdering";
import { DifficultyBadge } from "../../components/Difficulty/Difficulty";
import { QuestionStatusBadge } from "../../components/QuestionList/questionStatus";
import { Dash, QuestionSummary, ReviewCounts, SubjectCell } from "../../components/QuestionList/cells";
import { formatDate } from "../../utils/formatDate";
import { useTranslation } from "react-i18next";

// Drafts can't be moved to a folder, so they are not selectable (old payloads may lack status.code).
export const isDraftQuestion = (question) =>
  question?.status?.code ? question.status.code === "DRAFT" : question?.status?.name === "Draft";

// "First Last", or the username when both are empty (accounts that uploaded docx files have no name).
const authorName = (author) =>
  [author?.first_name, author?.last_name].filter(Boolean).join(" ").trim() || author?.username || "";

function Checkbox({ indeterminate = false, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:opacity-50"
      {...props}
    />
  );
}

/**
 * The question bank's table: the question (name, code, term and objective), its status and difficulty,
 * the subject (`showSubject`; with `showFolder`, the folder under it), subtopic, author, reviews, the
 * user's vote (`canVote`) and the date. The count and the pages are shown by the page (ResultsBar).
 */
export default function Table({
  questions,
  onStoreFilters,
  canVote = false,
  ordering,
  onSort,
  loading,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onTogglePage,
  showSubject = true,
  showFolder = false,
}) {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  // The checkboxes read the selection from here instead of rebuilding the columns: react-table renders
  // Header/Cell as components, so new functions on every toggle would remount the cells and drop the focus.
  const selectionRef = useRef(null);
  selectionRef.current = { questions, selectedIds };
  // The name links are built with the columns but must save the filters the page has now.
  const storeFiltersRef = useRef(onStoreFilters);
  storeFiltersRef.current = onStoreFilters;
  // Same for the passage chips: one opens its passage on the bank's Texts tab.
  const navigate = useNavigate();
  const openPassageRef = useRef(null);
  openPassageRef.current = (passage) => {
    onStoreFilters?.();
    navigate(`/questions/texts?open=${passage.uuid}`);
  };

  const renderMyVoteColumn = (question) => {
    if (!canVote || !question.evaluation) {
      return null;
    }

    const evaluationCode = question.evaluation.code;

    switch (evaluationCode) {
      case "UPVOTED":
        return (
          <div className="relative flex items-center justify-center" title={t("question_list.my_vote_up")}>
            <HandThumbUpIcon className="h-5 w-5 text-emerald-600" aria-hidden="true" />
            <span className="sr-only">{t("question_list.my_vote_up")}</span>
          </div>
        );
      case "DOWNVOTED":
        return (
          <div className="relative flex items-center justify-center" title={t("question_list.my_vote_down")}>
            <HandThumbDownIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
            <span className="sr-only">{t("question_list.my_vote_down")}</span>
          </div>
        );
      case "NONE":
      default:
        return (
          <div className="flex items-center justify-center">
            <MinusIcon className="h-5 w-5 text-slate-300" aria-hidden="true" />
          </div>
        );
    }
  };

  const columns = React.useMemo(() => {
    const cols = [];

    if (selectable) {
      cols.push({
        id: "select",
        // The label covers the whole cell (rows grow with two-line questions), so a click anywhere in it
        // toggles the checkbox instead of opening the question.
        cellClassName: "relative w-12",
        Header: () => {
          const selection = selectionRef.current;
          const pageSelectable = selection.questions.filter((q) => !isDraftQuestion(q));
          const selectedOnPage = pageSelectable.filter((q) => selection.selectedIds?.has(q.uuid)).length;
          return (
            <Checkbox
              aria-label={t("folders.select_page")}
              title={t("folders.select_page")}
              checked={pageSelectable.length > 0 && selectedOnPage === pageSelectable.length}
              indeterminate={selectedOnPage > 0 && selectedOnPage < pageSelectable.length}
              disabled={pageSelectable.length === 0}
              onChange={(e) => onTogglePage(pageSelectable, e.target.checked)}
            />
          );
        },
        Cell: (props) => {
          const question = props.row.original;
          const draft = isDraftQuestion(question);
          return (
            <label className={`absolute inset-0 flex items-center justify-center ${draft ? "cursor-not-allowed" : "cursor-pointer"}`}>
              <Checkbox
                aria-label={draft ? t("errors.drafts_not_allowed") : t("folders.select_question", { code: question.code || "" })}
                title={draft ? t("errors.drafts_not_allowed") : undefined}
                checked={!draft && !!selectionRef.current.selectedIds?.has(question.uuid)}
                disabled={draft}
                onChange={() => onToggleSelect(question)}
              />
            </label>
          );
        },
      });
    }

    cols.push(
      {
        Header: t("question_list.columns.question"),
        id: "question",
        accessor_for_ordering: "name",
        Cell: (props) => (
          <QuestionSummary
            question={props.row.original}
            href={`/questions/${props.row.original.uuid}`}
            onOpen={() => storeFiltersRef.current?.()}
            onPassage={(passage) => openPassageRef.current?.(passage)}
          />
        ),
      },
      {
        Header: t("question_list.columns.status"),
        id: "status",
        accessor_for_ordering: "status__name",
        Cell: (props) => <QuestionStatusBadge status={props.row.original.status} />,
      },
      {
        Header: t("difficulty.label"),
        accessor: "difficulty",
        Cell: (props) => (props.value ? <DifficultyBadge level={props.value} /> : <Dash />),
      },
    );

    if (showSubject) {
      cols.push({
        Header: t("question_list.columns.subject"),
        id: "subject",
        Cell: (props) => <SubjectCell question={props.row.original} showFolder={showFolder} />,
      });
    }

    cols.push(
      {
        Header: t("questions_page.table.subtopic"),
        accessor: "subtopic.name",
        Cell: (props) => (props.value ? <span className="text-slate-700">{props.value}</span> : <Dash />),
      },
      {
        Header: t("questions_page.table.author"),
        accessor: "author",
        Cell: (props) => <span className="text-slate-700">{authorName(props.value)}</span>,
      },
      {
        Header: t("questions_page.table.reviews"),
        id: "reviews",
        Cell: (props) => <ReviewCounts question={props.row.original} />,
      },
    );

    if (canVote) {
      cols.push({
        Header: t("questions_page.table.my_vote"),
        id: "myVote",
        Cell: (props) => renderMyVoteColumn(props.row.original),
      });
    }

    cols.push({
      Header: t("questions_page.table.created"),
      accessor: "created",
      accessor_for_ordering: "created",
      Cell: (props) => <span className="tabular-nums text-slate-500">{formatDate(props.value, language)}</span>,
    });

    return cols;
  }, [canVote, t, language, selectable, onToggleSelect, onTogglePage, showSubject, showFolder]);

  return (
    <TableWithServerOrdering
      columns={columns}
      data={questions}
      ordering={ordering}
      handleOrdering={onSort}
      loading={loading}
      hideCount
      dense
      getRowHref={(question) => `/questions/${question.uuid}`}
      onRowOpen={() => storeFiltersRef.current?.()}
    />
  );
}
